import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Card,
  Grid,
  Stack,
  Avatar,
  createSvgIcon,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { TokenContext } from "../context/TokenContext";
import { afasPoster } from "../hooks/useAfasDataMutation";
import { generateUUID, sha256, normalizeEmail, normalizePhone } from "../utils/crypto";
import { pushToDataLayer } from "../utils/gtm";
import type { GTMBrochureDownloadEvent } from "../types/types";
import { useParams } from "react-router-dom";

const StarOfLife = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 15 16">
    <path
      d="M14.5819 10.3616C14.8293 10.4853 14.9221 10.7946 14.7675 11.0421L13.7777 12.7432C13.654 12.9907 13.3447 13.0525 13.0972 12.9288L8.92164 10.5163V15.3414C8.92164 15.6198 8.6742 15.8363 8.42676 15.8363H6.4163C6.16886 15.8363 5.92141 15.6198 5.92141 15.3414V10.5163L1.74584 12.9288C1.4984 13.0525 1.18909 12.9907 1.06537 12.7432L0.0756072 11.0421C-0.0790438 10.7946 0.0137468 10.4853 0.261188 10.3616L4.43677 7.91813L0.261188 5.50558C0.0137468 5.38185 -0.0790438 5.07255 0.0756072 4.82511L1.06537 3.12395C1.18909 2.87651 1.4984 2.81465 1.74584 2.93837L5.92141 5.35092V0.494883C5.92141 0.247442 6.16886 0 6.4163 0H8.42676C8.6742 0 8.92164 0.247442 8.92164 0.494883V5.35092L13.0972 2.93837C13.3447 2.78372 13.654 2.87651 13.7777 3.12395L14.7675 4.82511C14.9221 5.07255 14.8293 5.38185 14.5819 5.50558L10.4063 7.91813L14.5819 10.3616Z"
      fill="currentColor"
    />
  </svg>,
  "Plus",
);

const EmailIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="14" viewBox="0 0 19 14" fill="none">
    <path
      d="M16.9167 0C17.8646 0 18.6667 0.802083 18.6667 1.75V12.25C18.6667 13.2344 17.8646 14 16.9167 14H1.75C0.765625 14 0 13.2344 0 12.25V1.75C0 0.802083 0.765625 0 1.75 0H16.9167ZM1.75 1.16667C1.42187 1.16667 1.16667 1.45833 1.16667 1.75V3.28125C1.93229 3.9375 3.09896 4.88542 6.63542 7.69271C7.25521 8.20312 8.45833 9.36979 9.33333 9.33333C10.1719 9.36979 11.375 8.20312 11.9948 7.69271C15.5312 4.88542 16.6979 3.9375 17.5 3.28125V1.75C17.5 1.45833 17.2083 1.16667 16.9167 1.16667H1.75ZM16.9167 12.8333C17.2083 12.8333 17.5 12.5781 17.5 12.25V4.77604C16.6615 5.46875 15.349 6.52604 12.724 8.60417C11.9583 9.22396 10.6458 10.5365 9.33333 10.5C7.98437 10.5365 6.67187 9.22396 5.90625 8.60417C3.28125 6.52604 1.96875 5.46875 1.16667 4.77604V12.25C1.16667 12.5781 1.42187 12.8333 1.75 12.8333H16.9167Z"
      fill="#41474D"
      fillOpacity="0.4"
    />
  </svg>,
  "Email",
);

const PhoneIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M0.743633 0.734278C0.297453 0.823514 0 1.18046 0 1.62664C0 9.1522 6.12754 15.2202 13.6234 15.2202C14.0695 15.2202 14.4265 14.9228 14.5157 14.5064L15.2296 11.5021C15.3188 11.0856 15.0809 10.6395 14.6942 10.461L11.4519 9.06297C11.0653 8.91424 10.6191 9.00348 10.3811 9.33068L9.07232 10.9369C6.99015 9.92558 5.32441 8.25984 4.31307 6.17767L5.91932 4.86888C6.24652 4.63091 6.33575 4.18473 6.18703 3.79805L4.789 0.555806C4.61052 0.139371 4.16434 -0.0688461 3.71817 0.0203899L0.743633 0.734278ZM13.5936 14.2684C6.63321 14.2684 0.981596 8.61679 0.981596 1.65638L3.92638 0.97224L5.29467 4.15499L3.12326 5.93971C4.58078 9.00348 6.24652 10.6692 9.31029 12.1267L11.095 9.95533L14.2778 11.3236L13.5936 14.2684Z"
      fill="#41474D"
      fillOpacity="0.4"
    />
  </svg>,
  "Phone",
);
// Simple email & phone validators matching the screenshot use‑case
const isEmail = (v: string) => /.+@.+\..+/.test(v.trim());
const isPhone = (v: string) => /^(\+\d{6,}|0\d{9,})$/.test(v.trim());
const isKvkValid = (v: string) => /^[0-9]{8}$/.test(v.trim());

// Custom field component with label on left, field in middle, required icon on right
interface CustomFieldProps {
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string | React.ReactNode;
  children: React.ReactNode;
}

const CustomField: React.FC<CustomFieldProps> = ({ label, required, error, helperText, children }) => {
  return (
    <Box>
      <Grid
        container
        spacing={{
          xs: 1,
          sm: 1,
          md: 3,
        }}
        alignItems="center"
      >
        {/* Label on the left */}
        <Grid size={{ xs: 12, sm: 3.9 }}>
          <Typography
            variant="body1"
            component={"label"}
            htmlFor={label.toLowerCase().replace(/\s+/g, "-")}
            color={error ? "warning" : "text.primary"}
            fontSize={{
              sx: "16px",
              md: "18px",
            }}
            sx={{
              fontWeight: 300,
              color: "#000",
              lineHeight: "normal",
            }}
          >
            {label}
          </Typography>
        </Grid>

        {/* Field in the middle */}
        <Grid size={{ xs: 12, sm: 8.1 }}>
          <Stack direction="row" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
            <Box sx={{ flex: "1 1 100%" }}>{children}</Box>

            {/* Required icon on the right */}
            {required && (
              <Avatar
                sx={{
                  bgcolor: "warning.main",
                  width: "22px",
                  height: "22px",
                  borderRadius: "2px",
                }}
              >
                <StarOfLife sx={{ width: "16px", height: "16px" }} />
              </Avatar>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Helper text below */}
      {helperText && (
        <Grid
          container
          spacing={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
        >
          <Grid size={{ xs: 12, sm: 3.9 }} />
          <Grid size={{ xs: 12, sm: 8.1 }}>
            <Typography
              variant="caption"
              color={error ? "warning" : "text.secondary"}
              sx={{
                display: "block",
                mt: 0.5,
                ml: 0,
                minHeight: "1.25em",
                fontSize: "12px",
              }}
            >
              {helperText}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export interface CompanyInfo {
  id: string;
  name: string;
  logo: string | false;
  details: string;
}

const MANUAL_KVK_OPTION: CompanyInfo = {
  id: "manual-kvk",
  name: "Staat jouw organisatie er niet bij?",
  logo: false,
  details: "Vul handmatig je KvK nummer in",
};

export default function BrochureForm() {
  const token = useContext(TokenContext);
  // const ignoreNextReset = useRef(false);
  const { id } = useParams<{ id: string }>(); // "/afspraak/:id"
  // const navigate = useNavigate();
  console.log({ id });

  // const referrerTest = document.referrer;
  // console.log({ referrerTest });
  const referer = "https://www.afas.nl/branche/handel";
  // temporary untill function getRedirectUrl is added
  const redirectUrl =
    "https://www.afas.nl/brochurepagina/erp-download-brochure-ingevuld-aanvraag-gelukt?kenmerk=534";

  // const isTokenReady = !!token;
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    orgName: "",
    orgKvk: "",
    employees: "",
    phone: "",
    type: "brochure",
  });

  const [formSessionId, setFormSessionId] = useState<string>("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isManualKvkMode, setIsManualKvkMode] = useState(false);
  const [needsManualDetails, setNeedsManualDetails] = useState(false);

  const errors = useMemo(() => {
    return {
      orgName: isManualKvkMode
        ? values.orgName.trim()
          ? ""
          : "Vul je bedrijfsnaam in"
        : values.orgName.trim()
          ? ""
          : "Selecteer of typ de naam van je organisatie",

      employees: values.employees ? "" : "Selecteer het aantal medewerkers",
      firstName: values.firstName.trim() ? "" : "Vul je voornaam in",
      lastName: values.lastName.trim() ? "" : "Vul je achternaam in",

      email: values.email.trim()
        ? isEmail(values.email)
          ? ""
          : "Vul een geldig e-mailadres in"
        : "Vul je e-mailadres in",

      orgKvk: needsManualDetails
        ? values.orgKvk.trim()
          ? isKvkValid(values.orgKvk)
            ? ""
            : "Vul een geldig KvK nummer in (8 cijfers)"
          : "Vul je KvK nummer in"
        : "",

      phone: values.phone.trim()
        ? isPhone(values.phone)
          ? ""
          : "Vul een geldig telefoonnummer in"
        : "Vul je telefoonnummer in",
    } as const;
  }, [values, isManualKvkMode, needsManualDetails]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [suggestedCompanies, setSuggestedCompanies] = useState<CompanyInfo[]>([MANUAL_KVK_OPTION]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyInfo | null>(null);
  const [companyInput, setCompanyInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isManualKvkMode) {
      setIsLoadingCompanies(false);
      return;
    }
    // only react to real "search" changes
    if (!searchTerm || searchTerm.trim().length <= 2) {
      setSuggestedCompanies([MANUAL_KVK_OPTION]);
      setIsLoadingCompanies(false);
      setNeedsManualDetails(false); // ✅
      return;
    }

    const handle = setTimeout(async () => {
      const orgName = searchTerm.trim(); // <-- use searchTerm here
      // console.log({ searchTerm, orgName });

      try {
        setIsLoadingCompanies(true);
        const path = "company";
        const arg = { token, body: orgName };
        const response = await afasPoster(path, { arg });

        // console.log("company response", response);

        let companies: CompanyInfo[] = [];

        // adjust to your real response shape; this is just defensive
        if (Array.isArray(response)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          companies = response.map((c: any) => ({
            id: String(c.id),
            name: String(c.name),
            logo: c.logo || false,
            details: String(c.details),
          }));
        } else if (Array.isArray(response.companies)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          companies = response.companies.map((c: any) => ({
            id: String(c.id),
            name: String(c.name),
            logo: c.logo || false,
            details: String(c.details),
          }));
        } else {
          console.warn("Unexpected company response shape", response);
        }

        // const realCompanies = companies.filter((c) => c.id !== "manual-kvk");
        setNeedsManualDetails(companies.length === 0);
        setSuggestedCompanies([...companies, MANUAL_KVK_OPTION]);
      } catch (e) {
        console.error("Error fetching company suggestions", e);
        setSuggestedCompanies([MANUAL_KVK_OPTION]);
        setNeedsManualDetails(true);
      } finally {
        setIsLoadingCompanies(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(handle);
  }, [searchTerm, token, isManualKvkMode]);

  // Generate form session ID once on component mount
  useEffect(() => {
    const sessionId = generateUUID();
    setFormSessionId(sessionId);
  }, []);

  const hasError = (k: keyof typeof errors) => Boolean(touched[k] && errors[k]);
  const allValid = Object.values(errors).every((e) => !e);

  const onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const { name, value } = e.target;
    setValues((s) => ({ ...s, [name]: value }));
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      orgName: true,
      employees: true,
      phone: true,
      ...(needsManualDetails && { orgKvk: true }),
    });
    if (!allValid) return;

    try {
      setIsSubmitting(true);

      // Normalize and hash user data for GTM tracking
      const normalizedEmail = normalizeEmail(values.email);
      const normalizedPhone = normalizePhone(values.phone);
      const hashedEmail = sha256(normalizedEmail);
      const hashedPhone = sha256(normalizedPhone);

      // Push event to GTM dataLayer
      const gtmEvent: GTMBrochureDownloadEvent = {
        event: "brochure_download",
        form_name: "brochure_aanvraag_homepage",
        form_session_id: formSessionId,
        form_data: {
          organisatie: values.orgName,
          aantal_medewerkers: values.employees,
        },
        user_data: {
          sha256_email_address: hashedEmail,
          sha256_phone_number: hashedPhone,
        },
      };
      pushToDataLayer(gtmEvent);

      // Wait 200ms for GTM to process the event
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Submit form to API (including formSessionId)
      const path = "form";
      const body = {
        values: { ...values, formSessionId, formid: id },
        referer,
      };
      console.log(body);
      const arg = { token, body };
      const response = await afasPoster(path, { arg });
      console.log(response);

      //eventuele redirect naar brochure of andere bedankt pagina.
      window.parent.location.href = redirectUrl;

      // alert(`Bedankt!\n\n${JSON.stringify(values, null, 2)}`);
    } catch (error) {
      console.error("Error while submitting form", error);
      // optionally show a toast / error message here
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetCompanyField = () => {
    setCompanyInput("");
    setSearchTerm("");
    setSelectedCompany(null);
    setIsManualKvkMode(false);
    setNeedsManualDetails(false);
    setSuggestedCompanies([MANUAL_KVK_OPTION]);

    setValues((s) => ({
      ...s,
      orgName: "",
      orgKvk: "",
    }));
  };

  return (
    <Card>
      {/* Form content */}
      <Box component="form" noValidate onSubmit={onSubmit}>
        <Box display="grid" gap={1}>
          <CustomField
            label="Bedrijfsnaam"
            required
            error={hasError("orgName")}
            helperText={hasError("orgName") ? errors.orgName : " "}
          >
            <Autocomplete<CompanyInfo, false, false, true>
              freeSolo
              id="bedrijfsnaam"
              clearText="Invoer wissen"
              openOnFocus
              options={suggestedCompanies}
              value={selectedCompany}
              inputValue={companyInput}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "clear") {
                  resetCompanyField();
                  return;
                }

                // MUI fires "reset" after selecting an option; ignore it because onChange handles selection
                if (reason === "reset") {
                  return;
                }

                if (reason !== "input") return;

                const nextValue = newInputValue;

                // typing means there is no confirmed selected company anymore
                if (selectedCompany) {
                  setSelectedCompany(null);
                }

                setCompanyInput(nextValue);
                setIsManualKvkMode(false);

                // while typing, don't force manual kvk yet
                if (values.orgKvk.trim() === "") {
                  setNeedsManualDetails(false);
                }

                setValues((s) => ({
                  ...s,
                  orgName: nextValue,
                  orgKvk: "",
                }));

                setSearchTerm(nextValue);
              }}
              onChange={(_, newValue) => {
                // clear icon / cleared value
                if (!newValue) {
                  resetCompanyField();
                  return;
                }

                // free text confirmed
                if (typeof newValue === "string") {
                  setSelectedCompany(null);
                  setCompanyInput(newValue);
                  setSearchTerm(newValue);
                  setIsManualKvkMode(false);

                  setValues((s) => ({
                    ...s,
                    orgName: newValue,
                    orgKvk: "",
                  }));

                  return;
                }

                // manual kvk option
                if (newValue.id === "manual-kvk") {
                  const preservedInput = companyInput.trim() || values.orgName.trim();

                  setSelectedCompany(null);
                  setIsManualKvkMode(true);
                  setNeedsManualDetails(true);
                  setSuggestedCompanies([MANUAL_KVK_OPTION]);
                  setSearchTerm("");
                  setCompanyInput(preservedInput);

                  setValues((s) => ({
                    ...s,
                    orgName: preservedInput,
                    orgKvk: "",
                  }));

                  return;
                }

                // picked real company
                setSelectedCompany(newValue);
                setCompanyInput(newValue.name);
                setSearchTerm("");
                setIsManualKvkMode(false);
                setNeedsManualDetails(false);

                setValues((s) => ({
                  ...s,
                  orgName: newValue.name,
                  orgKvk: String(newValue.id),
                }));
              }}
              onBlur={() => {
                setTouched((t) => ({ ...t, orgName: true }));

                // user typed something but did not select a real suggestion
                if (!selectedCompany && companyInput.trim().length > 0) {
                  setNeedsManualDetails(true);
                }
              }}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  padding: 0,
                  "& .MuiAutocomplete-input": {
                    padding: selectedCompany ? "0" : "8px 16px",
                    minHeight: "50px",
                  },
                },
              }}
              slotProps={{
                listbox: {
                  sx: {
                    p: 0,
                    pb: 0,
                  },
                },
              }}
              renderOption={(props, option, state) => {
                const { selected } = state;

                if (option.id === "manual-kvk") {
                  return (
                    <Box
                      component="li"
                      {...props}
                      key={option.id}
                      sx={{
                        position: "sticky",
                        bottom: "0",
                        left: "0",
                        zIndex: 1,
                        marginBottom: "0",
                        marginTop: ".5rem",
                        backgroundColor: "lightAccent.main",
                        padding: "20px 1rem",
                      }}
                    >
                      <Stack direction={"row"} spacing={0} alignItems={"center"}>
                        <AddIcon fontSize="small" />
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 0,
                            mt: 0,
                            fontWeight: 600,
                            fontSize: "15px",
                            lineHeight: "normal",
                            color: selected ? "primary.main" : "text.primary",
                          }}
                        >
                          {option.name}
                        </Typography>
                        {selected && (
                          <CheckIcon
                            sx={{
                              color: "primary.main",
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                }

                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      margin: 0,
                      padding: "14px 1rem",
                      transition: "all ease-in-out 250ms",
                      backgroundColor: selected
                        ? `${theme.palette.action.selected} !important`
                        : "transparent",
                      "&.Mui-selected": { backgroundColor: `${theme.palette.action.selected} !important` },
                      "&:hover": { backgroundColor: "action.hover" },
                      "&:hover .company-title": { color: "primary.main" },
                    })}
                  >
                    <Avatar
                      src={option.logo ? option.logo : ""}
                      alt={option.name}
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: "primary.main",
                      }}
                      variant="rounded"
                    >
                      {option.name.charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: "5px" }}>
                      <Typography
                        variant="body1"
                        className="company-title"
                        sx={{
                          mb: 0,
                          mt: 0,
                          fontWeight: 600,
                          transition: "all ease-in-out 250ms",
                          fontSize: "15px",
                          lineHeight: "normal",
                          color: selected ? "primary.main" : "text.primary",
                        }}
                      >
                        {option.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="dark.main"
                        sx={{
                          mb: 0,
                          mt: 0,
                          fontSize: "11px",
                        }}
                      >
                        {option.details}
                      </Typography>
                    </Box>

                    {selected && (
                      <CheckIcon
                        sx={{
                          color: "primary.main",
                        }}
                      />
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  disabled={isSubmitting}
                  error={hasError("orgName")}
                  placeholder="Bedrijfsnaam"
                  inputProps={{
                    ...params.inputProps,
                    readOnly: !!selectedCompany && selectedCompany.id !== "manual-kvk",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: selectedCompany ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          ml: 1,
                          mr: 1,
                          pl: 0.5,
                        }}
                      >
                        <Avatar
                          src={selectedCompany.logo ? selectedCompany.logo : ""}
                          alt={selectedCompany.name}
                          sx={{ width: 30, height: 30, backgroundColor: "primary.main" }}
                          variant="rounded"
                        >
                          {selectedCompany.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </Box>
                    ) : (
                      params.InputProps.startAdornment
                    ),
                    endAdornment: (
                      <>
                        {isLoadingCompanies && <CircularProgress size={20} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: hasError("orgName") ? "error.main" : "rgba(0, 95, 170, 0.20) !important",
                    },
                    "& .MuiOutlinedInput-input": {
                      paddingLeft: selectedCompany ? "8px" : "16px",
                    },
                  }}
                />
              )}
            />
          </CustomField>

          {/* Conditional KvK field - only shown when manual KvK option is selected */}
          {needsManualDetails && (
            <CustomField
              label="KvK nummer"
              required
              error={hasError("orgKvk")}
              helperText={hasError("orgKvk") ? errors.orgKvk : "8 cijfers, bijvoorbeeld: 12345678"}
            >
              <TextField
                name="orgKvk"
                id="kvk-nummer"
                disabled={isSubmitting}
                value={values.orgKvk}
                onChange={onChange}
                onBlur={() => setTouched((t) => ({ ...t, orgKvk: true }))}
                error={hasError("orgKvk")}
                variant="outlined"
                fullWidth
                placeholder="12345678"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: hasError("orgKvk") ? "error.main" : "rgba(0, 95, 170, 0.20) !important",
                  },
                }}
              />
            </CustomField>
          )}

          <CustomField
            label="Aantal medewerkers"
            required
            error={hasError("employees")}
            helperText={hasError("employees") ? errors.employees : " "}
          >
            <TextField
              name="employees"
              id="aantal-medewerkers"
              disabled={isSubmitting}
              value={values.employees} // ✅ FIX: was values.lastName
              onChange={(e) => {
                const raw = e.target.value;

                // keep only digits + limit to 6 chars
                const next = raw.replace(/\D/g, "").slice(0, 6);

                setValues((s) => ({ ...s, employees: next }));
              }}
              onBlur={() => setTouched((t) => ({ ...t, employees: true }))}
              error={hasError("employees")}
              variant="outlined"
              fullWidth
              placeholder="Aantal medewerkers"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 6,
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError("employees") ? "error.main" : "rgba(0, 95, 170, 0.20) !important",
                },
              }}
            />
          </CustomField>

          <CustomField
            label="Voornaam"
            required
            error={hasError("firstName")}
            helperText={hasError("firstName") ? errors.firstName : " "}
          >
            <TextField
              name="firstName"
              id="voornaam"
              disabled={isSubmitting}
              value={values.firstName}
              onChange={onChange}
              onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
              error={hasError("firstName")}
              variant="outlined"
              fullWidth
              placeholder="Voornaam"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError("firstName") ? "error.main" : "rgba(0, 95, 170, 0.20); !important",
                },
              }}
            />
          </CustomField>

          <CustomField
            label="Achternaam"
            required
            error={hasError("lastName")}
            helperText={hasError("lastName") ? errors.lastName : " "}
          >
            <TextField
              name="lastName"
              id="achternaam"
              disabled={isSubmitting}
              value={values.lastName}
              onChange={onChange}
              onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
              error={hasError("lastName")}
              variant="outlined"
              fullWidth
              placeholder="Achternaam"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError("lastName") ? "error.main" : "rgba(0, 95, 170, 0.20); !important",
                },
              }}
            />
          </CustomField>

          <CustomField
            label="E‑mail"
            required
            error={hasError("email")}
            helperText={hasError("email") ? errors.email : " "}
          >
            <TextField
              name="email"
              id="email"
              disabled={isSubmitting}
              value={values.email}
              onChange={onChange}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              error={hasError("email")}
              variant="outlined"
              fullWidth
              placeholder="mail@example.com"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <EmailIcon sx={{ fontSize: "20px" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError("email") ? "error.main" : "rgba(0, 95, 170, 0.20); !important",
                },
              }}
            />
          </CustomField>

          <CustomField
            label="Telefoonnummer"
            required
            error={hasError("phone")}
            helperText={hasError("phone") ? errors.phone : ""}
          >
            <TextField
              name="phone"
              id="telefoonnr"
              disabled={isSubmitting}
              placeholder="+31 6 12 34 56 78"
              value={values.phone}
              onChange={onChange}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              error={hasError("phone")}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PhoneIcon sx={{ fontSize: "20px" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: hasError("phone") ? "error.main" : "rgba(0, 95, 170, 0.20); !important",
                },
              }}
            />
          </CustomField>

          <Stack
            direction="row"
            justifyContent="space-between"
            mt={3}
            spacing={1}
            alignItems={"center"}
            flexWrap={"wrap"}
            useFlexGap
          >
            <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
              <Avatar
                sx={{
                  bgcolor: "warning.main",
                  width: "16px",
                  height: "16px",
                  borderRadius: "2px",
                }}
              >
                <StarOfLife sx={{ width: "10px", height: "10px" }} />
              </Avatar>
              <Typography fontSize={"14px"} variant="body1" color="initial">
                Dit zijn verplichte velden
              </Typography>
            </Stack>
            <Button type="submit" variant="contained" disabled={!allValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Bezig met verzenden...
                </>
              ) : (
                "Brochure aanvragen"
              )}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Card>
  );
}
